import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Info } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const isoOrEmpty = (d) => (d ? new Date(d).toISOString() : "");

// Iter316 — Common IANA timezones for the picker. Defaulted from the
// browser so most organisers won't need to touch it. Full IANA list is
// too long for a dropdown; we surface the popular ones and let the
// browser default fill anything niche.
const COMMON_TIMEZONES = [
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Amsterdam",
  "Europe/Berlin", "Europe/Madrid", "Europe/Rome", "Europe/Warsaw",
  "Europe/Stockholm", "Europe/Athens", "Europe/Istanbul", "UTC",
  "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Toronto", "America/Sao_Paulo",
  "Asia/Dubai", "Asia/Kolkata", "Asia/Singapore", "Asia/Hong_Kong",
  "Asia/Tokyo", "Asia/Shanghai", "Australia/Sydney", "Australia/Melbourne",
  "Pacific/Auckland",
];

const detectBrowserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// Midnight-crossing rule: any span that crosses a calendar-day boundary
// counts as an additional day. Noon-day1 → noon-day2 = 2 days. We work
// purely off the date part of the datetime-local strings so a user-tz
// choice doesn't shift the count. Client is best-effort; the backend
// recomputes canonically to prevent tampering.
const computePlannedDays = (start, end) => {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  if (e.getTime() <= s.getTime()) return 1;
  const sDate = new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const eDate = new Date(e.getFullYear(), e.getMonth(), e.getDate());
  const diff = Math.round((eDate.getTime() - sDate.getTime()) / 86400000);
  return Math.max(1, diff + 1);
};

export default function FestivalNew() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    template: "friendly_mix",
    start_at: "",
    end_at: "",
    timezone: detectBrowserTz(),
    venue_name: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Read-only, derived on the fly. Falls back to 1 when dates aren't set
  // yet so the badge always has something to render.
  const plannedDays = useMemo(
    () => computePlannedDays(form.start_at, form.end_at) ?? 1,
    [form.start_at, form.end_at],
  );

  // Iter316 — Block past-dated Starts. `datetime-local` inputs are
  // interpreted in the browser's local tz, so `new Date()` gives us the
  // matching wall-clock. We format it to the input's YYYY-MM-DDTHH:mm
  // shape for the `min` attribute (native picker greys out earlier
  // slots) AND re-check on submit so a manual paste can't bypass it.
  const nowLocalMin = useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }, []);
  const startInPast = !!form.start_at && new Date(form.start_at).getTime() < Date.now();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.start_at || !form.end_at) {
      toast.error("Name, start and end are required");
      return;
    }
    if (startInPast) {
      toast.error("Start date must be in the future");
      return;
    }
    if (new Date(form.end_at) <= new Date(form.start_at)) {
      toast.error("End must be after start");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/festival", {
        name: form.name.trim(),
        template: form.template,
        start_at: isoOrEmpty(form.start_at),
        end_at: isoOrEmpty(form.end_at),
        // planned_days is now server-derived from start/end/timezone;
        // we still send the client's computation for older backends and
        // so audit logs can spot mismatches, but the server overrides.
        planned_days: plannedDays,
        timezone: form.timezone || "UTC",
        venue_name: form.venue_name.trim() || null,
      });
      toast.success("Festival created");
      navigate(`/festival/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="festival-new">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 mb-4"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>
      <div className="label-mono text-[#CCFF00] mb-1">RALLY-LAB · NEW FESTIVAL</div>
      <h1 className="h-display text-3xl mb-6">Set up your event</h1>

      <form onSubmit={submit} className="card-surface p-6 space-y-5">
        <div>
          <label className="label-mono text-[#A3A3A3] block mb-1">Festival name</label>
          <input
            data-testid="festival-name-input"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Rally-Lab Summer Fest 2026"
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
          />
        </div>

        <div>
          <label className="label-mono text-[#A3A3A3] block mb-1">Template</label>
          <select
            data-testid="festival-template-select"
            value={form.template}
            onChange={(e) => set("template", e.target.value)}
            className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
          >
            <option value="friendly_mix">Friendly Mix (social, groups of 4)</option>
            <option value="match_scored" disabled>Match / Scored (coming in Phase 2)</option>
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-mono text-[#A3A3A3] block mb-1">Starts</label>
            <input
              type="datetime-local"
              data-testid="festival-start-input"
              value={form.start_at}
              onChange={(e) => set("start_at", e.target.value)}
              min={nowLocalMin}
              className={`w-full bg-[#0A0A0A] border px-3 py-2 focus:outline-none ${startInPast ? "border-[#FF4500] focus:border-[#FF4500]" : "border-[#262626] focus:border-[#CCFF00]"}`}
            />
            {/* Iter316 — Inline validation for past-dated Starts. Server
                also rejects so a manual paste can't slip through. */}
            {startInPast && (
              <div className="text-[11px] text-[#FF4500] mt-1" data-testid="festival-start-past-error">
                Start date must be in the future (your local time)
              </div>
            )}
          </div>
          <div>
            <label className="label-mono text-[#A3A3A3] block mb-1">Ends</label>
            <input
              type="datetime-local"
              data-testid="festival-end-input"
              value={form.end_at}
              onChange={(e) => set("end_at", e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-mono text-[#A3A3A3] block mb-1">Timezone</label>
            <select
              data-testid="festival-timezone-select"
              value={form.timezone}
              onChange={(e) => set("timezone", e.target.value)}
              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
              title="Used to compute planned days and unlock times"
            >
              {/* Ensure the detected browser tz is always the first pick even
                  if it isn't in COMMON_TIMEZONES, so the pre-select works. */}
              {!COMMON_TIMEZONES.includes(form.timezone) && (
                <option value={form.timezone}>{form.timezone} (your browser)</option>
              )}
              {COMMON_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-mono text-[#A3A3A3] block mb-1">Venue (optional)</label>
            <input
              data-testid="festival-venue-input"
              value={form.venue_name}
              onChange={(e) => set("venue_name", e.target.value)}
              placeholder="Rally-Lab HQ, London"
              className="w-full bg-[#0A0A0A] border border-[#262626] px-3 py-2 focus:outline-none focus:border-[#CCFF00]"
            />
          </div>
        </div>

        {/* Iter316 — Planned days is read-only auto-computed from
            Start / End using the midnight-crossing rule (a span crossing
            midnight counts as an additional day). Organisers can no
            longer edit this by hand; the server recomputes on save so
            activation-pass matching stays trustworthy. */}
        <div className="border border-[#262626] bg-[#0A0A0A] p-3 flex items-center gap-3" data-testid="festival-planned-days-readonly">
          <Info className="w-4 h-4 text-[#66C2FF] shrink-0" />
          <div className="flex-1">
            <div className="label-mono text-[#66C2FF] text-[10px]">EVENT DURATION</div>
            <div className="text-sm text-white">
              <span className="font-bold" data-testid="festival-planned-days-value">{plannedDays} day{plannedDays === 1 ? "" : "s"}</span>
              <span className="text-[#A3A3A3] ml-2 text-[11px]">auto from Start & End · any span across midnight counts as one more day</span>
            </div>
          </div>
        </div>

        <div className="pt-3 border-t border-[#262626] flex justify-end">
          <button
            type="submit"
            disabled={saving || startInPast}
            data-testid="festival-save"
            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-bold disabled:opacity-50"
            style={{ background: "#CCFF00", color: "#0A0A0A" }}
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Create festival"}
          </button>
        </div>
      </form>
    </div>
  );
}
